import { ArrowBack, ArrowForward } from '@mui/icons-material'
import {
  Box,
  Button,
  hexToRgb,
  Stack,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material'
import { FC, useEffect, useMemo, useState } from 'react'

import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import {
  EntryWithRelated,
  NotInspectedDummyEntry
} from '../../models/inspections'
import { useUpdateInspectionEntryMutation } from '../../redux/api/inspectionApi'
import { useGetInspectionEntryStatusQuery } from '../../redux/api/shopMatrixApi'
import { EntryInfoStatusEnum, InspectionEntryStatusEntry } from '../../sdk'
import {
  checkSinglePromptIsComplete,
  PromptError
} from '../../utils/checkInspectionEntryRequiredNotesOrPhoto'

interface Props {
  entry: EntryWithRelated
  submitCallback?: () => void
  goBackOneEntry?: () => void
}

const InspectionEntryStatusSelect: FC<Props> = ({
  entry,
  submitCallback,
  goBackOneEntry
}) => {
  const { accessToken, shopId } = useAccessTokenAndShopId()
  const { data: statuses } = useGetInspectionEntryStatusQuery({
    authorization: accessToken,
    shop: shopId,
    isActive: true
  })
  const [updateEntry] = useUpdateInspectionEntryMutation()

  const selectedStatus =
    entry.info!.customStatus || EntryInfoStatusEnum.NotInspected

  const allStatuses = useMemo(
    () =>
      !entry.info!.customStatus ||
      statuses?.find((s) => s.id === entry.info!.customStatus)
        ? statuses && [...statuses, NotInspectedDummyEntry]
        : statuses &&
          entry.status && [
            ...statuses,
            {
              ...entry.status,
              info: {
                ...entry.status.info!,
                title: entry.status.info!.title + ' (Outdated)'
              }
            },
            NotInspectedDummyEntry
          ],
    [entry.info, entry.status, statuses]
  )
  const prompt = entry.prompts[0]
  const [error, setError] = useState<PromptError>({
    status: false,
    answer: false,
    notes: false,
    photo: false
  })
  useEffect(() => {
    if (!prompt) {
      setError({ status: false, answer: false, notes: false, photo: false })
    } else {
      setError(checkSinglePromptIsComplete(prompt, entry))
    }
  }, [entry, prompt])

  const handleChange = async (status: InspectionEntryStatusEntry) => {
    await updateEntry({
      authorization: accessToken,
      entryEntry: {
        ...entry,
        info: {
          ...entry.info!,
          customStatus:
            status.id === EntryInfoStatusEnum.NotInspected
              ? undefined
              : status.id,
          complete:
            status.id === EntryInfoStatusEnum.NotInspected
              ? undefined
              : new Date()
        }
      }
    })
    if (submitCallback && status.id !== EntryInfoStatusEnum.NotInspected) {
      submitCallback()
    }
  }

  return (
    <>
      <ToggleButtonGroup value={selectedStatus} exclusive fullWidth>
        {allStatuses?.map((s) => (
          <ToggleButton
            key={s.id!}
            value={s.id!}
            onClick={(event, value) => {
              if (value) {
                handleChange(s)
              }
            }}
            sx={{
              '&.Mui-selected': {
                backgroundColor: hexToRgb('#' + s.info!.color!),
                '&:hover': {
                  backgroundColor: hexToRgb('#' + s.info!.color!)
                }
              },
              '&:hover': {
                backgroundColor: hexToRgb('#' + s.info!.color!)
              }
            }}
            size="small">
            {s.info!.title}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <Stack direction="row">
        {goBackOneEntry && (
          <Button
            variant="outlined"
            onClick={() => goBackOneEntry()}
            startIcon={<ArrowBack />}>
            Back
          </Button>
        )}
        <Box flex={1} />
        {submitCallback && (
          <Button
            variant="outlined"
            disabled={
              error.status || error.answer || error.notes || error.photo
            }
            onClick={() => submitCallback()}
            endIcon={<ArrowForward />}>
            Next
          </Button>
        )}
      </Stack>
    </>
  )
}

export default InspectionEntryStatusSelect
